import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';

interface ColDef {
  key: string;
  label: string;
  getValue: (row: Record<string, any>) => string;
  render?: (row: Record<string, any>) => React.ReactNode;
  isNumeric?: boolean;
  editable?: boolean;
}

interface Props {
  rows: Record<string, any>[];
  columns: ColDef[];
  emptyMessage?: string;
  toolbarLeft?: React.ReactNode;
  onBatchEdit?: (ids: string[], field: string, value: string | boolean) => void;
  onInvertAmount?: (ids: string[]) => void;
  onCellEdit?: (id: string, field: string, value: string | boolean) => void;
  onDelete?: (id: string) => void;
  classifierOptions?: {id: string; name: string; groupId: string}[];
  groupOptions?: {id: string; name: string}[];
  accountTypeOptions?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

const distinct = (values: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach(v => { const k = v.toLowerCase(); if (!seen.has(k)) { seen.add(k); result.push(v); } });
  return result.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

function matchesFilters(row: Record<string, any>, columns: ColDef[], filters: Record<string, string[]>, skipKey?: string): boolean {
  return columns.every(col => {
    if (skipKey && col.key === skipKey) return true;
    const f = filters[col.key];
    if (!f?.length) return true;
    return f.some(v => v.toLowerCase() === col.getValue(row).toLowerCase());
  });
}

function renderCellContent(col: ColDef, row: Record<string, any>): React.ReactNode {
  if (col.render) return col.render(row);
  return col.getValue(row);
}

function getRange(start: number, end: number): Set<number> {
  const min = Math.min(start, end);
  const max = Math.max(start, end);
  const s = new Set<number>();
  for (let i = min; i <= max; i++) s.add(i);
  return s;
}

const TransactionTable: React.FC<Props> = ({ rows, columns, emptyMessage = 'Nenhuma transação', toolbarLeft, onBatchEdit, onInvertAmount, onCellEdit, onDelete, classifierOptions = [], groupOptions = [], accountTypeOptions = [], onSelectionChange }) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState<{ key: string | null; dir: 'asc' | 'desc' | null }>({ key: null, dir: null });
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [hideExcluded, setHideExcluded] = useState(true);
  const [showUnclassified, setShowUnclassified] = useState(false);
  const [batchModal, setBatchModal] = useState(false);
  const [modalRows, setModalRows] = useState<Set<number>>(new Set());
  // Row drag selection — DOM-based for zero re-renders during drag
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const dragging = useRef(false);
  const dragStart = useRef(-1);
  const dragEnd = useRef(-1);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const highlightRows = useCallback((start: number, end: number) => {
    const tbody = tbodyRef.current;
    if (!tbody) return;
    const min = Math.min(start, end);
    const max = Math.max(start, end);
    const rows = tbody.children;
    for (let i = 0; i < rows.length; i++) {
      if (i >= min && i <= max) {
        rows[i].classList.add('bg-blue-50');
      } else {
        rows[i].classList.remove('bg-blue-50');
      }
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      if (!dragging.current) return;
      dragging.current = false;
      const start = dragStart.current;
      const end = dragEnd.current;
      const range = getRange(start, end);
      if (range.size >= 2) {
        setModalRows(range);
        setBatchModal(true);
      }
    };
    document.addEventListener('mouseup', handler);
    return () => document.removeEventListener('mouseup', handler);
  }, []);

  const handleRowMouseDown = useCallback((e: React.MouseEvent, rowIdx: number) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-drag]')) return;
    e.preventDefault();
    dragging.current = true;
    dragStart.current = rowIdx;
    dragEnd.current = rowIdx;
    highlightRows(rowIdx, rowIdx);
  }, [highlightRows]);

  const handleTbodyMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    const tr = (e.target as HTMLElement).closest('tr');
    if (!tr || !tbodyRef.current) return;
    const idx = Array.prototype.indexOf.call(tbodyRef.current.children, tr);
    if (idx < 0 || idx === dragEnd.current) return;
    dragEnd.current = idx;
    highlightRows(dragStart.current, idx);
  }, [highlightRows]);

  const setFilter = (k: string, v: string[]) => setFilters(p => ({ ...p, [k]: v }));

  const options = useMemo(() => {
    const o: Record<string, string[]> = {};
    columns.forEach(c => {
      const otherFiltered = rows.filter(r => matchesFilters(r, columns, filters, c.key));
      o[c.key] = distinct(otherFiltered.map(r => c.getValue(r)).filter(v => v !== ''));
    });
    return o;
  }, [rows, columns, filters]);

  const filtered = useMemo(() => {
    let base = rows.filter(r => matchesFilters(r, columns, filters));
    if (hideExcluded) base = base.filter(r => !r.excluded);
    if (showUnclassified) base = base.filter(r => !r.groupName && !r.classifierName);
    if (!sort.key || !sort.dir) return base;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return base;
    return [...base].sort((a, b) => {
      const av = col.getValue(a);
      const bv = col.getValue(b);
      const cmp = col.isNumeric
        ? parseFloat(av.replace(/[^\d.,-]/g, '').replace(',', '.')) - parseFloat(bv.replace(/[^\d.,-]/g, '').replace(',', '.'))
        : av.localeCompare(bv, undefined, { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, filters, sort, columns, hideExcluded, showUnclassified]);

  const toggleSort = (k: string) => setSort(p => {
    if (p.key !== k) return { key: k, dir: 'asc' };
    if (p.dir === 'asc') return { key: k, dir: 'desc' };
    return { key: null, dir: null };
  });

  const selectedIds = Array.from(selectedRows).map(i => filtered[i]?.id).filter(Boolean);
  const modalIds = Array.from(modalRows).map(i => filtered[i]?.id).filter(Boolean);

  const exportCsv = () => {
    const header = columns.map(c => c.label).join(';');
    const lines = filtered.map(row => columns.map(col => {
      const raw = row[col.key];
      const val = col.getValue ? col.getValue(row) : (raw ?? '');
      let str = String(val).replace(/;/g, ',').replace(/\n/g, ' ');
      const num = Number(raw);
      if (raw !== '' && raw != null && !isNaN(num) && (typeof raw === 'number' || /^-?\d+(\.\d+)?$/.test(String(raw)))) {
        str = num.toFixed(2).replace('.', ',');
      }
      return str;
    }).join(';'));
    const csv = '\uFEFF' + [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transacoes_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!onSelectionChange) return;
    const ids = Array.from(selectedRows).map(i => filtered[i]?.id).filter(Boolean);
    onSelectionChange(ids);
  }, [selectedRows, filtered, onSelectionChange]);

  const handleBatchApply = (classifierId: string, groupId: string, excluded: boolean, accountType?: string, date?: string, description?: string, amount?: number, adjustedAmount?: number | null) => {
    if (!onBatchEdit || modalIds.length === 0) return;
    if (classifierId.trim()) onBatchEdit(modalIds, 'classifier', classifierId);
    if (groupId.trim()) onBatchEdit(modalIds, 'group', groupId);
    onBatchEdit(modalIds, 'excluded', excluded as any);
    if (accountType) onBatchEdit(modalIds, 'accountType', accountType);
    if (date && onCellEdit && modalIds.length === 1) {
      onCellEdit(modalIds[0], 'date', date + ':00.000Z');
    }
    if (description && onBatchEdit && modalIds.length === 1) {
      onBatchEdit(modalIds, 'description', description);
    }
    if (amount !== undefined && onCellEdit && modalIds.length === 1) {
      const row = filtered[Array.from(modalRows)[0]];
      const sign = row && Number(row.amount) >= 0 ? 1 : -1;
      onBatchEdit(modalIds, 'amount', String(sign * Math.abs(amount)));
    }
    if (adjustedAmount !== undefined && onCellEdit && modalIds.length === 1) {
      onBatchEdit(modalIds, 'adjustedAmount', adjustedAmount !== null ? String(adjustedAmount) : '');
    }
    setBatchModal(false);
    setModalRows(new Set());
  };

  // Column visibility
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem('fin_hidden_cols'); if (s) return new Set(JSON.parse(s)); } catch {} return new Set();
  });
  const [colsMenuOpen, setColsMenuOpen] = useState(false);

  const toggleCol = (key: string) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem('fin_hidden_cols', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const visibleColumns = columns.filter(c => !hiddenCols.has(c.key));

  return (
    <div>
      {/* Mobile Toolbar */}
      <div className="sm:hidden space-y-2 mb-2">
        <div className="flex justify-end gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={hideExcluded} onChange={e => setHideExcluded(e.target.checked)} className="rounded" />{' '}
            Desconsiderar
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showUnclassified} onChange={e => setShowUnclassified(e.target.checked)} className="rounded" />{' '}
            Não Classificados
          </label>
        </div>
        <div className="border rounded-lg bg-gray-50 overflow-hidden">
          <button type="button" onClick={() => setColsMenuOpen(!colsMenuOpen)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600">
            <span>Colunas visíveis</span>
            <svg className={`w-4 h-4 transition-transform ${colsMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {colsMenuOpen && (
            <div className="px-3 pb-2 grid grid-cols-2 gap-1 border-t pt-2">
              {columns.map(col => (
                <label key={col.key} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer py-0.5">
                  <input type="checkbox" checked={!hiddenCols.has(col.key)} onChange={() => toggleCol(col.key)} className="rounded w-3.5 h-3.5" />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <div>{toolbarLeft}</div>
      </div>
      {/* Mobile Cards */}
      <div className="sm:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">{emptyMessage}</div>
        ) : filtered.map((row, rowIdx) => (
          <MobileCard key={row.id || rowIdx} row={row} columns={columns}
            selected={selectedRows.has(rowIdx)}
            onTap={() => { setModalRows(new Set([rowIdx])); setBatchModal(true); }}
            onDelete={onDelete && row.source === 'manual' ? () => onDelete(row.id) : undefined}
            userHiddenCols={hiddenCols}
          />
        ))}
        <div className="px-3 py-2 text-xs text-gray-500 flex justify-between items-center">
          <span>{filtered.length} de {rows.length} transações</span>
          <button onClick={exportCsv} className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">Exportar CSV</button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:flex sm:flex-col sm:items-center">
        <div className="w-fit max-w-full">
          <div className="flex items-center justify-between mb-2">
            <div>{toolbarLeft}</div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={hideExcluded} onChange={e => setHideExcluded(e.target.checked)} className="rounded" />{' '}
                Desconsiderar
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={showUnclassified} onChange={e => setShowUnclassified(e.target.checked)} className="rounded" />{' '}
                Não Classificados
              </label>
              <div className="relative">
                <button onClick={() => setColsMenuOpen(!colsMenuOpen)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Colunas">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </button>
                {colsMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-lg p-2 min-w-[160px]">
                    {columns.map(col => (
                      <label key={col.key} className="flex items-center gap-2 py-1 px-1 text-xs cursor-pointer hover:bg-gray-50 rounded">
                        <input type="checkbox" checked={!hiddenCols.has(col.key)} onChange={() => toggleCol(col.key)} className="rounded" />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            {selectedIds.length > 0 && (() => {
              let income = 0, expense = 0;
              for (const id of selectedIds) {
                const row = filtered.find(r => r.id === id);
                if (!row || row.excluded) continue;
                const amt = Number(row.amount) || 0;
                if (amt >= 0) income += amt;
                else expense += Math.abs(amt);
              }
              const balance = income - expense;
              return (
                <div className="px-3 py-2 bg-blue-50 text-xs border-b flex items-center gap-4 font-medium">
                  <span className="text-gray-700">Selecionadas: {selectedIds.length}</span>
                  <span className="text-green-600">Receitas: {income.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  <span className="text-red-600">Despesas: {expense.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  <span className={balance >= 0 ? 'text-blue-600' : 'text-orange-600'}>Saldo: {balance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                </div>
              );
            })()}
            <div className="overflow-x-auto [transform:rotateX(180deg)]">
              <table className="text-sm [transform:rotateX(180deg)]">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-2 py-2 text-center" data-no-drag>
                <input
                  type="checkbox"
                  className="rounded cursor-pointer"
                  checked={filtered.length > 0 && selectedRows.size === filtered.length}
                  ref={el => { if (el) el.indeterminate = selectedRows.size > 0 && selectedRows.size < filtered.length; }}
                  onChange={e => {
                    if (e.target.checked) {
                      const all = new Set<number>();
                      for (let i = 0; i < filtered.length; i++) all.add(i);
                      setSelectedRows(all);
                    } else {
                      setSelectedRows(new Set());
                    }
                  }}
                  title="Selecionar todos"
                  aria-label="Selecionar todos"
                />
              </th>
              {visibleColumns.map((col, colIdx) => (
                <th key={col.key} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap relative">
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleSort(col.key)} className="hover:text-blue-600">
                      {col.label}
                      {sort.key === col.key && (sort.dir === 'asc' ? ' ↑' : ' ↓')}
                    </button>
                    <button
                      onClick={() => setOpenFilter(openFilter === col.key ? null : col.key)}
                      className={`text-xs px-1 rounded ${filters[col.key]?.length ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      ▼
                    </button>
                  </div>
                  {openFilter === col.key && (
                    <FilterDropdown
                      options={options[col.key] || []}
                      selected={filters[col.key] || []}
                      onChange={v => { setFilter(col.key, v); setOpenFilter(null); }}
                      onClose={() => setOpenFilter(null)}
                    />
                  )}
                </th>
              ))}
              {onDelete && <th className="px-2 py-2" />}
            </tr>
          </thead>
          <tbody ref={tbodyRef} onMouseMove={handleTbodyMouseMove}>
            {filtered.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 2} className="px-4 py-8 text-center text-gray-500">{emptyMessage}</td></tr>
            ) : filtered.map((row, rowIdx) => (
              <tr key={row.id || rowIdx}
                className={`border-b select-none cursor-pointer ${selectedRows.has(rowIdx) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => handleRowMouseDown(e, rowIdx)}
                onDoubleClick={() => { setModalRows(new Set([rowIdx])); setBatchModal(true); }}
              >
                <td className="px-2 py-2 text-center" data-no-drag>
                  <input
                    type="checkbox"
                    className="rounded cursor-pointer"
                    checked={selectedRows.has(rowIdx)}
                    onChange={e => {
                      e.stopPropagation();
                      setSelectedRows(prev => {
                        const next = new Set(prev);
                        if (next.has(rowIdx)) next.delete(rowIdx);
                        else next.add(rowIdx);
                        return next;
                      });
                    }}
                    aria-label={`Selecionar linha ${rowIdx + 1}`}
                  />
                </td>
                {visibleColumns.map((col, colIdx) => (
                  <td key={col.key} className="px-3 py-2 whitespace-nowrap"
                    data-no-drag={col.key === 'excluded' ? true : undefined}
                    onClick={col.key === 'excluded' && onCellEdit ? (e) => { e.stopPropagation(); onCellEdit(row.id, 'excluded', !row.excluded); } : undefined}
                  >
                    {renderCellContent(col, row)}
                  </td>
                ))}
                {onDelete && row.source === 'manual' && (
                  <td className="px-2 py-2">
                    <button onClick={(e) => { e.stopPropagation(); onDelete(row.id); }} className="text-red-400 hover:text-red-600 text-xs" title="Apagar">✕</button>
                  </td>
                )}
                {onDelete && row.source !== 'manual' && <td className="px-2 py-2" />}
              </tr>
            ))}
          </tbody>
              </table>
            </div>
            <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 border-t flex justify-between items-center">
              <span>{filtered.length} de {rows.length} transações</span>
              <button onClick={exportCsv} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Exportar CSV</button>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Edit Overlay */}
      {batchModal && (() => {
        const singleRow = modalRows.size === 1 ? filtered[Array.from(modalRows)[0]] : undefined;
        return (
        <BatchEditOverlay
          count={modalRows.size}
          classifierOptions={classifierOptions}
          groupOptions={groupOptions}
          accountTypeOptions={accountTypeOptions.map(t => ({id: t, name: t}))}
          initialDate={singleRow?.date ? new Date(String(singleRow.date)).toISOString().slice(0, 16) : undefined}
          isManual={singleRow?.source === 'manual'}
          initialDescription={singleRow ? String(singleRow.description || '') : undefined}
          initialAmount={singleRow ? Number(singleRow.amount) : undefined}
          initialAdjustedAmount={singleRow ? (singleRow.adjustedAmount !== undefined && singleRow.adjustedAmount !== null ? Number(singleRow.adjustedAmount) : null) : undefined}
          initialGroupId={singleRow ? String(singleRow.groupId || '') : undefined}
          initialClassifierId={singleRow ? String(singleRow.classifierId || '') : undefined}
          initialAccountType={singleRow ? String(singleRow.accountType || '') : undefined}
          initialExcluded={singleRow?.excluded === true}
          onApply={handleBatchApply}
          onInvert={() => { if (onInvertAmount) { onInvertAmount(modalIds); setBatchModal(false); setModalRows(new Set()); } }}
          onCancel={() => { setBatchModal(false); setModalRows(new Set()); }}
        />);
      })()}
    </div>
  );
};

const MOBILE_HIDDEN_COLS = new Set(['category', 'accountName', 'accountId', 'accountType', 'type', 'source', 'excluded']);

const COL_ICONS: Record<string, string> = {
  date: '📅', description: '📝', group: '📂', classifier: '🏷️',
  amount: '💰', category: '📋', accountName: '🏦', accountId: '🔑', accountType: '💳', type: '↕️', source: '🔗', excluded: '🚫',
};

function getMobileValue(col: ColDef, row: Record<string, any>): React.ReactNode {
  if (col.key === 'accountName' && row.account) return row.account.number || '';
  return renderCellContent(col, row);
}

const MobileCard: React.FC<{ row: Record<string, any>; columns: ColDef[]; selected: boolean; onTap: () => void; onDelete?: () => void; userHiddenCols: Set<string> }> = ({ row, columns, selected, onTap, onDelete, userHiddenCols }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedCols = columns.filter(c => !userHiddenCols.has(c.key));
  const visibleCols = selectedCols.filter(c => !MOBILE_HIDDEN_COLS.has(c.key));
  const expandableCols = selectedCols.filter(c => MOBILE_HIDDEN_COLS.has(c.key));

  return (
    <div className={`border rounded-lg overflow-hidden ${selected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}>
      {/* Main content */}
      <div className="p-3 space-y-0.5">
        {visibleCols.map(col => (
          <div key={col.key} className="text-sm flex items-center gap-1.5 overflow-hidden">
            <span className="text-xs shrink-0">{COL_ICONS[col.key] || '•'}</span>
            <span className="truncate">{getMobileValue(col, row)}</span>
          </div>
        ))}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-1">
          {expandableCols.map(col => (
            <div key={col.key} className="flex justify-between text-xs items-center overflow-hidden">
              <span className="text-gray-500 flex items-center gap-1 shrink-0"><span>{COL_ICONS[col.key] || '•'}</span>{col.label}</span>
              <span className="text-gray-900 truncate ml-2">{getMobileValue(col, row)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="px-3 pb-3 flex gap-2">
        <button onClick={onTap}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          <span>Editar</span>
        </button>
        {onDelete && (
          <button onClick={onDelete}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            <span>Excluir</span>
          </button>
        )}
        {expandableCols.length > 0 && (
          <button onClick={() => setExpanded(!expanded)}
            className="w-10 h-auto flex items-center justify-center text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        )}
      </div>
    </div>
  );
};

const BatchEditOverlay: React.FC<{
  count: number;
  classifierOptions: {id: string; name: string; groupId: string}[];
  groupOptions: {id: string; name: string}[];
  accountTypeOptions: {id: string; name: string}[];
  initialDate?: string;
  isManual?: boolean;
  initialDescription?: string;
  initialAmount?: number;
  initialAdjustedAmount?: number | null;
  initialGroupId?: string;
  initialClassifierId?: string;
  initialAccountType?: string;
  initialExcluded?: boolean;
  onApply: (classifierId: string, groupId: string, excluded: boolean, accountType?: string, date?: string, description?: string, amount?: number, adjustedAmount?: number | null) => void;
  onInvert: () => void;
  onCancel: () => void;
}> = ({ count, classifierOptions, groupOptions, accountTypeOptions, initialDate, isManual, initialDescription, initialAmount, initialAdjustedAmount, initialGroupId, initialClassifierId, initialAccountType, initialExcluded, onApply, onInvert, onCancel }) => {
  const [groupId, setGroupId] = useState(initialGroupId || '');
  const [classifierId, setClassifierId] = useState(initialClassifierId || '');
  const [excluded, setExcluded] = useState<boolean>(initialExcluded === true);
  const [accountType, setAccountType] = useState(initialAccountType || '');
  const [date, setDate] = useState(initialDate || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [amount, setAmount] = useState(initialAmount !== undefined ? String(Math.abs(initialAmount)) : '');
  const [adjustedAmount, setAdjustedAmount] = useState<string>(
    initialAdjustedAmount !== undefined && initialAdjustedAmount !== null ? String(initialAdjustedAmount) : ''
  );

  const filteredCls = groupId ? classifierOptions.filter(c => c.groupId === groupId) : classifierOptions;
  const clsDisplayOptions = filteredCls.map(c => {
    if (groupId) return { id: c.id, name: c.name };
    const grpName = groupOptions.find(a => a.id === c.groupId)?.name || '';
    return { id: c.id, name: grpName ? `${c.name} (${grpName})` : c.name };
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full sm:max-w-sm p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="font-semibold text-gray-900">{count === 1 ? 'Editar Transação' : 'Edição em lote'}</h3>
        <p className="text-xs text-gray-500">{count} linha(s) selecionada(s)</p>
        {count === 1 && (
          <div>
            <label htmlFor="batch-desc" className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
            <input id="batch-desc" type="text" value={description} onChange={e => setDescription(e.target.value)}
              readOnly={!isManual}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isManual ? 'bg-gray-100 cursor-default' : ''}`} />
          </div>
        )}
        <div className="space-y-3">
          {count === 1 && (
            <div>
              <label htmlFor="batch-date" className="block text-xs font-medium text-gray-700 mb-1">Data/Hora</label>
              <input id="batch-date" type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          )}
          {count === 1 && isManual && (
              <div>
                <label htmlFor="batch-amount" className="block text-xs font-medium text-gray-700 mb-1">Valor</label>
                <input id="batch-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
          )}
          {count === 1 && (
            <div>
              <label htmlFor="batch-adjusted-amount" className="block text-xs font-medium text-gray-700 mb-1">Valor Ajustado</label>
              <input
                id="batch-adjusted-amount"
                type="number"
                step="0.01"
                value={adjustedAmount}
                onChange={e => setAdjustedAmount(e.target.value)}
                placeholder="0,00 (usa valor original se vazio)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <SelectField label="Agrupador" value={groupId} onChange={v => { setGroupId(v); setClassifierId(''); }} options={groupOptions} autoFocus={count > 1} />
          <SelectField label="Classificação" value={classifierId} onChange={v => { setClassifierId(v); if (v && !groupId) { const cls = classifierOptions.find(c => c.id === v); if (cls?.groupId) setGroupId(cls.groupId); } }} options={clsDisplayOptions} />
          <SelectField label="Tipo Conta" value={accountType} onChange={setAccountType} options={accountTypeOptions} />
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={excluded} onChange={e => setExcluded(e.target.checked)} className="rounded" />{' '}
            Desconsiderar
          </label>
        </div>
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button onClick={onInvert} className="w-full sm:w-auto px-3 py-3 sm:py-2 text-xs bg-orange-600 text-white rounded-lg hover:bg-orange-700">
            Inverter Valor (×-1)
          </button>
          <div className="flex w-full sm:w-auto gap-2">
            <button onClick={onCancel} className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button onClick={() => onApply(classifierId, groupId, excluded, accountTypeOptions.find(t => t.id === accountType)?.name || undefined, date || undefined, description.trim() || undefined, amount ? parseFloat(amount) : undefined, count === 1 ? (adjustedAmount.trim() !== '' ? Number(adjustedAmount.replace(',', '.')) : null) : undefined)} disabled={!classifierId && !groupId && excluded === initialExcluded && !accountType && !date && !description.trim() && !amount} className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Aplicar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SelectField: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: {id: string; name: string}[]; autoFocus?: boolean }> = ({ label, value, onChange, options, autoFocus }) => {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select autoFocus={autoFocus} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
        <option value="">— Selecione —</option>
        {options.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
      </select>
    </div>
  );
};

const FilterDropdown: React.FC<{ options: string[]; selected: string[]; onChange: (v: string[]) => void; onClose: () => void }> = ({ options, selected, onChange, onClose }) => {
  const [local, setLocal] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const toggle = (v: string) => setLocal(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const filteredOpts = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 min-w-[200px] max-h-[300px] flex flex-col">
      <div className="flex gap-1 mb-2">
        <button onClick={() => { onChange([]); }} className="text-xs text-blue-600 hover:underline">Limpar</button>
        <button onClick={() => { onChange(local); }} className="text-xs ml-auto bg-blue-600 text-white px-2 py-0.5 rounded">Aplicar</button>
        <button onClick={onClose} className="text-xs text-gray-500 hover:underline">✕</button>
      </div>
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar..."
        className="w-full border border-gray-200 rounded px-2 py-1 text-xs mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
      />
      <div className="overflow-y-auto flex-1">
        {filteredOpts.map(opt => (
          <label key={opt} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer hover:bg-gray-50 rounded px-1">
            <input type="checkbox" checked={local.includes(opt)} onChange={() => toggle(opt)} className="rounded" />
            <span className="truncate">{opt || '(vazio)'}</span>
          </label>
        ))}
        {filteredOpts.length === 0 && <p className="text-xs text-gray-400 py-2 text-center">Nenhum resultado</p>}
      </div>
    </div>
  );
};

export default TransactionTable;
export type { ColDef };
